import { useImperativeHandle, useState, useRef, useCallback } from "react";
import BagBubble from "./BagBubble";
import { ButtonName, getDefaultProductImage } from "@brandclub/common-ui";
import { useTheme } from "@mui/material";
import { useCartCount } from "@/api/cache/cartQueryOptions";
import { ShopAppButton } from "../ShopAppButton";

export interface AnimationInstance {
  id: number;
  imageUrl: string;
  color?: string;
}

type AnimationRefObj = {
  current: {
    addToBagAnimation: (
      sku?: string,
      imageBase64?: string,
      successCallback?: () => void
    ) => void;
  } | null;
};

let refs: AnimationRefObj[] = [];

const addNewRef = (newRef: AnimationRefObj["current"]) => {
  refs.push({
    current: newRef,
  });
};

const removeOldRef = (oldRef: AnimationRefObj["current"]) => {
  refs = refs.filter((r) => r.current !== oldRef);
};

const BagButton = ({ onClick }: { onClick: () => Promise<void> }) => {
  const { data: cartCount = 0 } = useCartCount();
  const [animations, setAnimations] = useState<AnimationInstance[]>([]);
  const recieveAnimateRequest = async (sku?: string, imageBase64?: string) => {
    let image = "";
    if (imageBase64) {
      image = imageBase64;
    } else if (sku) {
      image = getDefaultProductImage(sku);
    }

    if (!image) {
      return;
    }

    const newAnimation: AnimationInstance = {
      id: animations.length,

      imageUrl: image,
    };

    setAnimations((prevAnimation) => [...prevAnimation, newAnimation]);
  };

  const animationRef = useRef<AnimationRefObj["current"]>(null);

  const setRef = useCallback((ref: AnimationRefObj["current"]) => {
    if (ref) {
      animationRef.current = ref;
      addNewRef(ref);
    } else {
      removeOldRef(animationRef.current);
    }
  }, []);

  useImperativeHandle(
    setRef,
    useCallback(
      () => ({
        addToBagAnimation: recieveAnimateRequest,
      }),
      [recieveAnimateRequest]
    )
  );
  const theme = useTheme();

  return (
    <ShopAppButton
      variant="solid"
      color="tertiary"
      buttonName={ButtonName.BagButton}
      height={{ xs: 32, md: 37 }}
      sx={(theme) => ({
        padding: 0,
        ...(Object.values(
          theme.componentCSSOverrides?.[ButtonName.BagButton] ?? {}
        ).some((style) => !!style.outlineColor) && {
          // only apply outline if the outline color is set
          outlineStyle: "solid",
          outlineWidth: "1px",
          outlineOffset: "7px",
          marginLeft: "7px",
          marginRight: "7px",
          // outline color is already set in the button, see the cssStyleOverrides applied to the SolidButton in Button.tsx (parseLegacyDtcThemeResponse.ts adds the outline color from the legacy theme)
        }),
      })}
      className={`bc-box-border bc-flex bc-justify-center bc-items-center bc-w-[32px] bc-min-w-[32px] desktop:bc-w-[37px] bc-rounded-full bc-border`}
      onClick={onClick}
      key="bag-button"
    >
      <span
        className={`bc-box-border bc-flex bc-justify-center bc-items-center  bc-w-[32px]  desktop:bc-w-[37px] bc-h-[32px] desktop:bc-h-[37px] bc-rounded-full`}
        style={{
          fontSize: theme.typography.fontSize,
          fontWeight: theme.settings?.fonts?.fontWeight,
        }}
      >
        {cartCount}
      </span>
      {animations.map((animation) => {
        return (
          <BagBubble
            key={animation.id}
            color={theme.vars.palette.tertiary.main}
            {...animation}
          ></BagBubble>
        );
      })}
    </ShopAppButton>
  );
};

function getRef() {
  const reversePriority = [...refs].reverse();
  const activeRef = reversePriority.find((ref) => ref?.current !== null);
  if (!activeRef) {
    return null;
  }
  return activeRef.current;
}

BagButton.AnimateAddProductToBag = (sku?: string, imageBase64?: string) => {
  getRef()?.addToBagAnimation(sku, imageBase64);
};

export default BagButton;
